import React from 'react'
import classNames from 'classnames/bind'

import useFieldfolioPrices from 'hooks/useFieldfolioPrices'
import Logo from 'components/Logo'
import Icon from 'components/Icon'

import styles from './MarketingPricingPanel.module.css'

const cx = classNames.bind(styles)

type MarketingPricingPanelProps = {
  className?: string
}

const formatCurrency = (number: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(number)
}

const Cell = ({
  children,
  isTinted = false,
  icon
}: {
  children?: React.ReactNode
  isTinted?: boolean
  icon?: string
}) => (
  <div className={cx(styles.cell, styles.hideMobile, { isTinted })}>
    {children && <div className={styles.cellText}>{children}</div>}
    {icon === 'checkCircle' && <Icon kind="check-circle" size={16} color="var(--colorGreen8)" />}
    {icon === 'dollarSign' && <Icon kind="dollar-sign" size={16} color="var(--colorYellow8)" />}
    {icon === 'x' && <Icon kind="x" size={16} color="var(--colorRed8)" />}
  </div>
)

const MarketingPricingPanel = ({ className }: MarketingPricingPanelProps) => {
  const { getRecurringPlanPricing, getIncludedAddonsCount } = useFieldfolioPrices({})

  const basePrice = getRecurringPlanPricing({ component: 'base', currency: 'AUD' })
  const includedReps = getIncludedAddonsCount({ component: 'reps' })
  const includedStaff = getIncludedAddonsCount({ component: 'staff' })

  const REP_COUNT = includedReps
  const REP_PRICE = 75
  const ORDER_COUNT = 30
  const ORDER_VALUE = 800
  const COMMISSION = 0.15

  const salesTotal = formatCurrency(ORDER_VALUE * ORDER_COUNT)
  const commissionTotal = formatCurrency(ORDER_VALUE * ORDER_COUNT * COMMISSION)

  return (
    <>
      <div className={cx(styles.container, className)}>
        <div className={cx(styles.column, styles.orderCountColumn)}>
          <Cell isTinted>Unlimited Products</Cell>
          <Cell>Unlimited Customers</Cell>
          <Cell isTinted>B2B Marketplace</Cell>
          <Cell>Integrations & API</Cell>
          <Cell isTinted>iPad App</Cell>
          <Cell>Sales Rep Seats</Cell>
          <Cell isTinted>Admin Staff Seats</Cell>
        </div>

        <div className={cx(styles.column, styles.fieldfolio)}>
          <div className={cx(styles.cell, styles.headerCell)}>
            <Logo />
          </div>
          <div className={cx(styles.cell, styles.priceCell)}>
            <div className={cx(styles.priceNumber, styles.fieldfolioPrice)}>${basePrice}</div>
            <div className={styles.priceDescriptor}>Billed Monthly</div>
          </div>
          <Cell isTinted icon="checkCircle" />
          <Cell icon="checkCircle" />
          <Cell isTinted icon="checkCircle" />
          <Cell icon="checkCircle" />
          <Cell isTinted icon="checkCircle" />
          <Cell>{includedReps} Included</Cell>
          <Cell isTinted>{includedStaff} Included</Cell>
        </div>

        <div className={styles.column}>
          <div className={cx(styles.cell, styles.headerCell)}>
            Commission-based
            <br />
            marketplaces
          </div>

          <div className={cx(styles.cell, styles.priceCell)}>
            <div className={styles.priceNumber}>{commissionTotal}</div>
            <div className={styles.priceDescriptor}>
              {COMMISSION * 100}% of {salesTotal} <sup>†</sup>
            </div>
          </div>
          <Cell isTinted icon="checkCircle" />
          <Cell icon="checkCircle" />
          <Cell isTinted icon="checkCircle" />
          <Cell icon="dollarSign" />
          <Cell isTinted icon="x" />
          <Cell icon="x" />
          <Cell isTinted icon="x" />
        </div>

        <div className={styles.column}>
          <div className={cx(styles.cell, styles.headerCell)}>Mobile sales apps</div>
          <div className={cx(styles.cell, styles.priceCell)}>
            <div className={styles.priceNumber}>{formatCurrency(REP_COUNT * REP_PRICE)}</div>
            <div className={styles.priceDescriptor}>
              {REP_COUNT} Sales Reps <sup>‡</sup>
            </div>
          </div>
          <Cell isTinted icon="dollarSign" />
          <Cell icon="dollarSign" />
          <Cell isTinted icon="x" />
          <Cell icon="checkCircle" />
          <Cell isTinted icon="checkCircle" />
          <Cell icon="dollarSign" />
          <Cell isTinted icon="checkCircle" />
        </div>
      </div>
      <div className={styles.qualifiers}>
        * Pricing shown in Australian Dollars (AUD) excluding GST.
        <br />
        <sup>†</sup> Based on an average order value of ${ORDER_VALUE} and {ORDER_COUNT} orders per month.
        <br />
        <sup>‡</sup> Based on a per Sales Rep price of ${REP_PRICE} per month.
      </div>
    </>
  )
}

export default MarketingPricingPanel
